import './controls-guide.css';

export type ControlsGuideProps = {
  variant: 'hud' | 'loading';
  inputMode: 'pointer' | 'drag';
  pointerLocked?: boolean;
  pointerFallback?: boolean;
};

function MovementGlyph() {
  return (
    <span className="input-glyph-wasd" aria-label="W、A、S、D 键">
      <kbd className="input-glyph-key input-glyph-key--w">W</kbd>
      <kbd className="input-glyph-key">A</kbd>
      <kbd className="input-glyph-key">S</kbd>
      <kbd className="input-glyph-key">D</kbd>
    </span>
  );
}

function MouseGlyph({ highlightButton }: { highlightButton: boolean }) {
  return (
    <svg className="input-glyph-mouse" viewBox="0 0 80 64" fill="none" aria-hidden="true">
      <path className={highlightButton ? 'input-glyph-mouse-button input-glyph-mouse-button--active' : 'input-glyph-mouse-button'} d="M40 11C32.3 11 26 17.3 26 25v5h14V11Z" />
      <rect x="26" y="11" width="28" height="43" rx="14" />
      <path d="M26 30h28M40 11v19" />
      <path className="input-glyph-mouse-wheel" d="M40 18v6" />
      <path className="input-glyph-mouse-arrow" d="M17 25v15m-4-11 4-4 4 4m-8 7 4 4 4-4M63 25v15m-4-11 4-4 4 4m-8 7 4 4 4-4" />
      <path className="input-glyph-mouse-arrow" d="M33 4h14m-10-3-4 3 4 3m6-6 4 3-4 3M33 61h14m-10-3-4 3 4 3m6-6 4 3-4 3" />
    </svg>
  );
}

export default function ControlsGuide({
  variant,
  inputMode,
  pointerLocked = false,
  pointerFallback = false,
}: ControlsGuideProps) {
  const dragMode = inputMode === 'drag' || pointerFallback;
  const lookHint = dragMode
    ? '按住左键拖动视角'
    : pointerLocked
      ? '移动鼠标转动视角'
      : '点击画面，开始鼠标转向';
  if (variant === 'hud') {
    return (
      <section className="controls-guide controls-guide--hud" aria-label="键盘和鼠标操作提示">
        <div className="controls-guide-main">
          <div className="controls-guide-group">
            <MovementGlyph />
            <span className="controls-guide-label">移动</span>
          </div>
          <div className="controls-guide-group">
            <MouseGlyph highlightButton={dragMode || !pointerLocked} />
            <div className="controls-guide-copy">
              <span className="controls-guide-label">{dragMode ? '拖动环顾' : '环顾'}</span>
              {(dragMode || !pointerLocked) && <span className="controls-guide-hint">{dragMode ? '按住左键' : '点击画面开始'}</span>}
            </div>
          </div>
        </div>
        <span className="controls-guide-sr-only">也可使用方向键移动。{lookHint}。Space 跳跃。{dragMode ? 'Esc 暂停。' : 'Esc 暂停并释放鼠标。'}</span>
      </section>
    );
  }

  return (
    <section className={`controls-guide controls-guide--${variant}`} aria-label="键盘和鼠标操作指引">
      <div className="controls-guide-header">
        <h2 className="controls-guide-heading">用键盘和鼠标探索展厅</h2>
      </div>
      <div className="controls-guide-main">
        <div className="controls-guide-group">
          <MovementGlyph />
          <div className="controls-guide-copy">
            <span className="controls-guide-label">移动</span>
            <span className="controls-guide-hint">或方向键</span>
          </div>
        </div>
        <div className="controls-guide-group">
          <MouseGlyph highlightButton={dragMode || !pointerLocked} />
          <div className="controls-guide-copy">
            <span className="controls-guide-label">环顾展厅</span>
            <span className="controls-guide-hint">{lookHint}</span>
          </div>
        </div>
      </div>
      <div className="controls-guide-shortcuts">
        <span className="controls-guide-shortcut"><kbd className="input-glyph-key">E</kbd><span>靠近展品后打开</span></span>
        <span className="controls-guide-shortcut"><kbd className="input-glyph-key input-glyph-key--wide">Space</kbd><span>跳跃</span></span>
        <span className="controls-guide-shortcut"><kbd className="input-glyph-key input-glyph-key--wide">Esc</kbd><span>{dragMode ? '暂停' : '暂停 / 释放鼠标'}</span></span>
      </div>
      <p className="controls-guide-first-step">{dragMode ? '载入后，按住鼠标左键拖动来环顾。' : '载入后，点击展厅画面开始环顾。'}</p>
    </section>
  );
}
